import { ReactComponent as FacebookIcon } from 'assets/image/svg/Facebook.svg';
import { ReactComponent as TwitterIcon } from 'assets/image/svg/Twitter.svg';
import { ReactComponent as InstagramIcon } from 'assets/image/svg/Instagram.svg';
import { ReactComponent as LinkedInIcon } from 'assets/image/svg/LinkedIn.svg';
import { PRIVACY_POLICY_URL, routePaths, TERMS_OF_SERVICE_URL } from '../utils/constants';
import { useSiteConfig } from 'src/contexts/SiteGuardContext';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import AppImage from 'components/image/AppImage';
import { getSiteSupportContact, telHref } from 'src/utils/supportAvailability.util';
import { useEffect, useState } from 'react';
const MOBILE = 768;

const Footer = ({ viewMode }: { viewMode?: 'desktop' | 'mobile' }) => {
   const { siteConfig } = useSiteConfig();
   const [isMobile, setIsMobile] = useState((typeof window !== 'undefined' ? window.innerWidth : 0) < MOBILE);
   const isMobileMode = viewMode === 'mobile';
   const { t } = useTranslation();
   const footerLogo = siteConfig?.visualBranding?.footerLogo;
   const displayName = siteConfig?.displayName || 'Nonprofit Trips';
   const { phone: supportPhone, email: supportEmail } = getSiteSupportContact(siteConfig);

   useEffect(() => {
      const handleResize = () => {
         const checkMobile = window.innerWidth < MOBILE;
         if (checkMobile !== isMobile) setIsMobile(checkMobile);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, [isMobile]);

   return (
      <footer className={twMerge('px-8 py-6 max-lg:p-4 bg-footer flex flex-col gap-6 max-lg:gap-4 w-full', isMobileMode && 'p-4')}>
         <div className={twMerge('flex max-md:flex-col items-start justify-between gap-4', isMobileMode && 'flex-col')}>
            <div className={twMerge('flex flex-col gap-4 w-[200px] max-md:w-full', isMobileMode && 'w-full')}>
               <a href={routePaths.LANDING }>
                  <AppImage
                     src={footerLogo}
                     alt={`${displayName} Logo`}
                     className={twMerge('h-6.5 w-auto text-white', viewMode === 'mobile' ? '' : 'md:mb-0')}
                  />
               </a>
               <div className="text-xs leading-[18px] max-md:leading-3 text-footerSubtext">{t('footer.clearRates')}</div>
               <div className="flex items-center gap-2.5">
                  <div className="social-media h-8.5 w-8.5 rounded-lg border border-footerTicket/50 hover:bg-footerTicket flex items-center justify-center cursor-pointer">
                     <FacebookIcon />
                  </div>
                  <div className="social-media h-8.5 w-8.5 rounded-lg border border-footerTicket/50 hover:bg-footerTicket flex items-center justify-center cursor-pointer">
                     <TwitterIcon />
                  </div>
                  <div className="social-media h-8 w-8.5 rounded-lg border border-footerTicket/50 hover:bg-footerTicket flex items-center justify-center cursor-pointer">
                     <InstagramIcon />
                  </div>
                  <div className="social-media h-8.5 w-8.5 rounded-lg border border-footerTicket/50 hover:bg-footerTicket flex items-center justify-center cursor-pointer">
                     <LinkedInIcon />
                  </div>
               </div>
            </div>
            {!isMobile && !isMobileMode && <Company />}
            {!isMobile && !isMobileMode && <Support />}
            {(isMobile || isMobileMode) && (
               <div className="flex w-full">
                  <Company className="flex-1" />
                  <Support className="flex-1" />
               </div>
            )}
            <div
               className={twMerge(
                  'p-6 max-lg:p-4 max-md:p-6 border border-footerSubtext/30 bg-lPrimary/5 rounded-xl flex flex-col gap-2.5 max-md:w-full',
                  isMobileMode && 'w-full',
               )}
            >
               <div className=" uppercase font-bold text-l-xs text-footerSubtext tracking-[3px]">{t('footer.tripSupport')}</div>
               {/* NPT-2081: phone, email and one line of hours. Contact details come from the site
                   config only (NPT-1929/NPT-2013); a missing one is not shown. */}
               {supportPhone && (
                  <a href={telHref(supportPhone)} className="text-l-3xl text-white font-bebas">
                     {supportPhone}
                  </a>
               )}
               {supportEmail && (
                  <a href={`mailto:${supportEmail}`} className="text-white text-l-sm underline break-all">
                     {supportEmail}
                  </a>
               )}
               {(supportPhone || supportEmail) && <div className="text-footerBorder text-l-sm max-w-[320px]">{t('footer.supportHours')}</div>}
               <div className="text-footerBorder text-l-sm">
                  A <span className="font-semibold">{t('footer.justGoodTravel')}</span> <span className="lowercase">{t('footer.company')}</span>
               </div>
            </div>
         </div>
         <div className="pt-6 max-lg:pt-4 border-t border-footerTicket/50">
            <div className="text-footerSubtext text-l-xs">
               © {new Date().getFullYear()} {displayName}. {t('footer.allRightsReserved')}
            </div>
         </div>
      </footer>
   );
};

export default Footer;

export const Company = ({ className }: { className?: string }) => {
   const { siteConfig } = useSiteConfig();
   const { t } = useTranslation();
   const displayName = siteConfig?.displayName || 'Nonprofit Trips';

   // URLs
   const aboutUrl = siteConfig?.visualBranding?.legal?.aboutUrl || '/about';
   const whoWeAreUrl = siteConfig?.visualBranding?.legal?.whoWeAreUrl || '/who-we-are';
   const forSiteNameUrl = siteConfig?.visualBranding?.legal?.forSiteNameUrl || '/about';

   // Labels
   const aboutLabel = siteConfig?.visualBranding?.legal?.aboutLabel || 'About';
   const whoWeAreLabel = siteConfig?.visualBranding?.legal?.whoWeAreLabel || 'Who We Are';
   const forSiteNameLabel = `For ${siteConfig?.visualBranding?.legal?.forSiteNameLabel || displayName}`;

   return (
      <div className={twMerge('flex flex-col gap-3.5', className)}>
         <div className="text-l-xs uppercase text-lPrimary font-bold tracking-[3px]">{t('footer.company')}</div>
         <a href={aboutUrl} className="text-white text-l-sm">
            {aboutLabel}
         </a>
         <a href={whoWeAreUrl} className="text-white text-l-sm">
            {whoWeAreLabel}
         </a>
         <a href={routePaths.LANDING} className="text-white text-l-sm">
            Find Your Org
         </a>
         <a href={forSiteNameUrl} className="text-white text-l-sm">
            {forSiteNameLabel}
         </a>
      </div>
   );
};
export const Support = ({ className }: { className?: string }) => {
   const { siteConfig } = useSiteConfig();
   const { t } = useTranslation();
   // URLs
   const privacyUrl = siteConfig?.visualBranding?.legal?.privacyPolicyUrl || PRIVACY_POLICY_URL;
   const termsUrl = siteConfig?.visualBranding?.legal?.termsOfServiceUrl || TERMS_OF_SERVICE_URL;
   const faqUrl = siteConfig?.visualBranding?.legal?.faqsUrl || '/faq';

   // Labels
   const faqLabel = siteConfig?.visualBranding?.legal?.faqsLabel || 'FAQ';
   const privacyLabel = siteConfig?.visualBranding?.legal?.privacyPolicyLabel || 'Privacy';
   const termsLabel = siteConfig?.visualBranding?.legal?.termsOfServiceLabel || 'Legal';

   return (
      <div className={twMerge('flex flex-col gap-3.5', className)}>
         <div className="text-l-xs uppercase text-lPrimary font-bold tracking-[3px]">{t('footer.support')}</div>
         <a href={faqUrl} className="text-white text-l-sm">
            {faqLabel}
         </a>
         <a href={privacyUrl} className="text-white text-l-sm">
            {privacyLabel}
         </a>
         <a href={termsUrl} className="text-white text-l-sm">
            {termsLabel}
         </a>
      </div>
   );
};
